import { useCallback, useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import { Outlet } from 'react-router-dom'
import { parseAsNativeArrayOf, parseAsString, useQueryState } from 'nuqs'

import { useQuery } from '@apollo/client/react'

import {
  Button,
  CalendarIcon,
  CriticalIcon,
  EmptyState,
  ListFilterIcon,
  PageHeader,
  PageLayout,
  pageHeaderStyles,
  PlusIcon,
  Text,
  useLocalStorage,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { useContentItemDraftsCount } from '~publish/components/ContentItemCard'
import { ProductPageTitle } from '~publish/components/ProductPageTitle'
import {
  ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
  AllChannelsTimeZoneSelect,
} from '~publish/components/AllChannelsTimeZoneSelect'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import {
  FilterByChannel,
  FilterByChannel_Channel,
  NO_CHANNELS_VALUE,
} from '~publish/components/FilterByChannel'
import {
  FilterByTag,
  filterOutUntaggedId,
} from '~publish/components/FilterByTag'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { NewPostHeaderActions } from '~publish/components/NewPostHeaderActions'
import { OpenChannelStoreFrontTrigger } from '~publish/components/OpenChannelStoreFrontTrigger'
import { GuestDraftImportNotice } from '~publish/components/GuestDraftHandoff'
import { PageNotices } from '~publish/components/PageNotices'
import { PostListOrCalendarViewToggle } from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'
import {
  PostTabs,
  PostTabsResponsive,
  PostEmptyStateByTab,
  isPostTab,
} from '~publish/components/PostTabs'
import type { PostTab } from '~publish/components/PostTabs'
import { RegisteredBannersProvider } from '~publish/components/RegisteredBanner'
import { ViewOnlyHeaderBadge } from '~publish/components/ViewOnlyBadge'
import {
  useHasOrganizationAction,
  useIsOwner,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { isService } from '~publish/helpers/channels'
import { mapPostStatusToTab } from '~publish/helpers/post'
import { trackQueueOpened } from '~publish/helpers/queueCalendarTracking'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'
import { TrackRender } from '~publish/hooks/tracking'
import { TimezoneProvider } from '~publish/hooks/useTimezone'
import { useFullStory } from '~publish/legacy/thirdParty/hooks/useFullStory'
import { canPerform } from '~publish/utils/permissions/canPerform'
import {
  hasOnlyInaccessibleChannels,
  selectEnabledChannelIds,
} from '~publish/utils/permissions/effectiveChannelSelection'
import {
  getDisabledChannelIds,
  getDisabledPublishChannelReason,
} from '~publish/utils/permissions/getDisabledChannelIds'
import {
  areAllChannelsRestrictedForPublish,
  areAllChannelsViewOnlyForPublish,
  getViewOnlyPublishChannelIds,
  VIEW_ONLY_PUBLISH_CHANNEL_TOOLTIP,
} from '~publish/utils/permissions/viewOnlyPublish'

import { AllChannelsAccessDenied } from './AllChannelsAccessDenied'
import { AllChannelsPausedQueueNotice } from './AllChannelsPausedQueueNotice'
import ManageChannelAccessCTA from './ManageChannelAccessCTA/ManageChannelAccessCTA'
import { NoChannelsFilterEmptyState } from './NoChannelsFilterEmptyState'
import {
  FilteredPostListEmptyState,
  PostList,
  PostListSkeleton,
} from './PostList'
import { usePostCounts } from './PostList/usePostCounts'
import type { PostStatus } from './PostList/usePaginatedPostList'
import { QueueActions } from './QueueActions'
import TeamMemberInviteUpgradeCTA from './TeamMemberInviteUpgradeCTA/TeamMemberInviteUpgradeCTA'
import { ValueLedQueueEmptyState } from './ValueLedQueueEmptyState'

import styles from './AllChannelsPage.module.css'

const POST_STATUSES: PostStatus[] = [
  'draft',
  'scheduled',
  'error',
  'needs_approval',
  'sent',
  'sending',
]

const SECTION_HEADING_BY_POST_TAB: Record<PostTab, string> = {
  queue: 'Queue',
  drafts: 'Drafts',
  approvals: 'Approvals',
  sent: 'Sent',
}

function mapTabQueryParamToTab(value: string): PostTab {
  const status = POST_STATUSES.find((candidate) => candidate === value)
  return status ? mapPostStatusToTab(status) : 'queue'
}

export const GetAllChannelsInfo = graphql(
  /* GraphQL */ `
    query GetAllChannelsInfo {
      # TODO: channels should be a top-level query
      account {
        id
        currentOrganization {
          id
          channels(input: { filter: { isLocked: false } }) {
            id
            name
            service
            isQueuePaused
            timezone
            products
            allowedActions
            postingSchedule {
              day
              times
              paused
            }
            ...FilterByChannel_Channel
          }
          onboarding {
            responses {
              stepSlug
              selectedOptions
              skipped
            }
          }
        }
      }
    }
  `,
  [FilterByChannel_Channel],
)

export const AllChannelsPage = (): JSX.Element => {
  const { initFullStory } = useFullStory({ onlyBetaUsers: true })
  const [allChannelsTimeZone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )

  const organizationId = useOrganizationId()
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')
  const isOwner = useIsOwner()

  // URL query parameters. `channels`/`tags` keep their `[]`-suffixed key and
  // multi-value shape so GlobalButton, Insights, Community, and saved views —
  // which still read them through the legacy useQueryParam hook — parse the
  // same URL regardless of which hook wrote it.
  const [rawTab, setTab] = useQueryState(
    'tab',
    parseAsString.withDefault('queue').withOptions({ history: 'push' }),
  )
  const tab: PostTab = isPostTab(rawTab)
    ? rawTab
    : mapTabQueryParamToTab(rawTab)
  const [channelQueryParam, setChannelFilterParam] = useQueryState(
    'channels[]',
    parseAsNativeArrayOf(parseAsString).withOptions({ history: 'push' }),
  )
  const [tagsQueryParam, setTagFilterParam] = useQueryState(
    'tags[]',
    parseAsNativeArrayOf(parseAsString).withOptions({ history: 'push' }),
  )

  const isNoChannelsSelected = channelQueryParam.includes(NO_CHANNELS_VALUE)
  const selectedRealChannelIds = channelQueryParam.filter(
    (id) => id !== NO_CHANNELS_VALUE,
  )

  // "No channels" adds channel-less content items to the drafts list
  // alongside any real channel selection, and a tag filter narrows them like
  // any other draft — so each setter leaves the other filter dimension as is.
  const setChannelFilter = useCallback(
    (channelIds: string[]): void => {
      void setChannelFilterParam(channelIds)
    },
    [setChannelFilterParam],
  )
  const setTagFilter = useCallback(
    (tagIds: string[]): void => {
      void setTagFilterParam(tagIds)
    },
    [setTagFilterParam],
  )
  const clearFilters = useCallback((): void => {
    void setChannelFilterParam([])
    void setTagFilterParam([])
  }, [setChannelFilterParam, setTagFilterParam])

  const {
    data,
    error,
    loading: channelsLoading,
  } = useQuery(GetAllChannelsInfo, {
    skip: !organizationId,
  })
  const channels = sanitizeNullableArray(
    data?.account?.currentOrganization?.channels,
  )
  const onboardingChannelFocus =
    data?.account?.currentOrganization?.onboarding?.responses
      .find(
        (response) =>
          response.stepSlug === 'channels-in-focus' && !response.skipped,
      )
      ?.selectedOptions.filter(isService) ?? null
  const pausedChannels = channels.filter((channel) => channel.isQueuePaused)

  // The query returns unlocked channels the actor can view, including those
  // without Publish support.
  // `viewPublish` is the publish-access gate for the Publish surface — a
  // channel the actor can't publish to is greyed-out in the filter (rather
  // than throwing when selected), and when the actor can publish to none of
  // them the whole page is replaced by the Access Restricted state.
  // (Reading post data itself is the broader `readUpdates` gate that
  // `findUpdates` enforces, but the Publish surface stays publish-only.)
  const disabledChannelIds = useMemo(
    () => getDisabledChannelIds(channels),
    [channels],
  )
  const viewOnlyChannelIds = useMemo(
    () => getViewOnlyPublishChannelIds(channels),
    [channels],
  )
  const hasChannelsButNoAccess = areAllChannelsRestrictedForPublish(channels)

  const isViewOnly = areAllChannelsViewOnlyForPublish(channels)
  const showComposerTrigger = !isViewOnly
  const valueLedQueueChannels = useMemo(
    () =>
      channels
        .filter((channel) => canPerform(channel, 'manageUpdates'))
        .map((channel) => ({
          id: channel.id,
          name: channel.name,
          service: channel.service,
          timezone: channel.timezone,
          isQueuePaused: channel.isQueuePaused,
          postingSchedule: channel.postingSchedule,
        })),
    [channels],
  )
  const canShowValueLedQueue = !isViewOnly

  // The URL is user-editable state (saved views, bookmarks, hand-typed
  // channels[]), so the selection the page acts on — dropdown, tab counts,
  // post list, composer prefill — is its intersection with the channels the
  // actor can view in Publish. @see selectEnabledChannelIds
  //
  // "No channels" is a distinct sentinel, not a channel id — passing the raw,
  // sentinel-inclusive selection would make `selectEnabledChannelIds` silently
  // drop it (it's unknown to the org's channel list) and would make
  // `hasOnlyInaccessibleChannels` read the resulting empty intersection as
  // "selected channels the actor can't access". Filtering it out here keeps
  // it out of a function shared with Community and Insights, which have no
  // such concept.
  const effectiveChannelIds = useMemo(
    () =>
      selectEnabledChannelIds(
        selectedRealChannelIds,
        channels,
        disabledChannelIds,
      ),
    [channels, selectedRealChannelIds, disabledChannelIds],
  )

  // Distinct from `effectiveChannelIds`: the channel filter UIs need the
  // sentinel itself to render "No channels" as checked, but every data hook
  // needs `effectiveChannelIds` (the real channels only) to know which
  // channels' drafts to fetch alongside the channel-less ones. Kept in the
  // value on every tab (not just Drafts, where `showNoChannelsOption` alone
  // hides the row) — otherwise picking a real channel from a non-Drafts tab
  // would read `isNoChannelsSelected` as false there and silently drop the
  // sentinel from the URL on the next selection change.
  const channelFilterSelection = isNoChannelsSelected
    ? [...effectiveChannelIds, NO_CHANNELS_VALUE]
    : effectiveChannelIds

  // Distinct from `hasChannelsButNoAccess` (org-wide): the org has
  // publishable channels, but the explicit selection contains none. Shows
  // the access state rather than silently widening `[]` back to "all
  // channels", and suppresses tab counts that would otherwise fall back to
  // org-wide totals. Skipped while "No channels" is selected — an
  // inaccessible real channel alongside it still leaves channel-less content
  // to show, so this must not block the whole page.
  const selectionHasNoAccessibleChannels =
    !isNoChannelsSelected &&
    hasOnlyInaccessibleChannels(
      selectedRealChannelIds,
      effectiveChannelIds,
      channels,
    )

  // "No channels" with no *accessible* real channel alongside it fetches no
  // posts at all — every tab's post count and post list would otherwise
  // widen an empty `effectiveChannelIds` back to "all channels". Based on
  // `effectiveChannelIds`, not `selectedRealChannelIds`: a stale/inaccessible
  // channel id in the URL must still count as "no real channels", or these
  // tabs silently widen instead of showing the drafts-only notice.
  const isPureNoChannelsSelection =
    isNoChannelsSelected && effectiveChannelIds.length === 0

  useEffect(() => {
    if (
      tab !== 'queue' ||
      channelsLoading ||
      !organizationId ||
      hasChannelsButNoAccess ||
      selectionHasNoAccessibleChannels
    ) {
      return
    }

    trackQueueOpened(organizationId)
  }, [
    channelsLoading,
    hasChannelsButNoAccess,
    organizationId,
    selectionHasNoAccessibleChannels,
    tab,
  ])

  const { counts: fetchedCounts, loading: countsLoading } = usePostCounts({
    organizationId,
    channelIds: effectiveChannelIds.length ? effectiveChannelIds : undefined,
    tagIds: tagsQueryParam,
  })
  // No post is ever channel-less, so a pure "No channels" selection (no real
  // channel alongside it) fetches no posts at all — nulling `usePostCounts`'
  // result here reads as 0 on every tab (drafts' count is patched back in
  // below) instead of widening back to org-wide totals.
  const counts =
    selectionHasNoAccessibleChannels || isPureNoChannelsSelection
      ? null
      : fetchedCounts

  const filterApplied =
    channelQueryParam.length > 0 || tagsQueryParam.length > 0

  // A channel-less draft has no channel, so a channel filter leaves it out
  // unless "No channels" is selected — matching what the drafts list renders.
  // Tags narrow it like any other draft.
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const {
    count: channelLessDraftsCount,
    loading: channelLessDraftsCountLoading,
  } = useContentItemDraftsCount({
    skip:
      !channelAgnosticDraftsEnabled ||
      (channelQueryParam.length > 0 && !isNoChannelsSelected),
    tagIds: tagsQueryParam,
  })

  useEffect(
    function checkTabQueryParam() {
      if (!isPostTab(rawTab)) {
        // `replace`, not the params' default `push`: this is a one-time URL
        // normalization, not a user-driven filter change, so it shouldn't
        // leave a history entry the back button gets stuck re-normalizing.
        void setTab(mapTabQueryParamToTab(rawTab), { history: 'replace' })
      }
    },
    [setTab, rawTab],
  )

  // tracking
  useEffect(() => {
    initFullStory()
  }, [initFullStory])

  // HOTFIX: this event was causing aa lot of traffic and us hitting Mixpanel limits
  // TODO: Create different tracking for filters and tab switches
  // See: https://linear.app/buffer/issue/CONT-158
  // useEffect(() => {
  // BufferTracker.allChannelsOpened({
  //   tab,
  //   organizationId,
  //   accountId: data?.account?.id || '',
  //   channelsSelected:
  //     channelQueryParam.length === 0 ? undefined : channelQueryParam,
  //   tagsSelected: tagsQueryParam.length === 0 ? undefined : tagsQueryParam,
  // })
  // }, [organizationId, channelQueryParam, tagsQueryParam, tab, data?.account])

  if (organizationId && error) {
    return (
      <EmptyState size="xlarge" variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Failed to load</EmptyState.Heading>
        <EmptyState.Description>
          Error happened, please let our team know about it.{' '}
          <Text color="critical">{error.message}</Text>
        </EmptyState.Description>
      </EmptyState>
    )
  }

  if (hasChannelsButNoAccess) {
    return <AllChannelsAccessDenied />
  }

  // `postCounts` counts posts per channel, so channel-less drafts are missing
  // from its draft total and are added here. `counts` is null both when the
  // org has no connected channels and when "No channels" is the active
  // filter — in both cases channel-less drafts still exist, so this falls
  // back to their count rather than dropping them.
  const draftsCount =
    counts != null
      ? counts.drafts + channelLessDraftsCount
      : channelLessDraftsCount || undefined

  const tabs: { id: PostTab; count: number | null | undefined }[] = [
    { id: 'queue', count: counts?.queue },
    { id: 'drafts', count: draftsCount },
    { id: 'approvals', count: counts?.approvals },
    { id: 'sent', count: counts?.sent },
  ]

  const tabCountLimit = counts?.limit

  return (
    <>
      <TimezoneProvider timeZone={allChannelsTimeZone}>
        <RegisteredBannersProvider
          key={`registered-banner-provider-allChannels`}
        >
          <PostTabs
            asChild
            value={tab}
            onChange={(value): void => void setTab(value)}
          >
            <PageLayout className={styles.page}>
              <PageNotices>
                {pausedChannels.length > 0 && (
                  <AllChannelsPausedQueueNotice
                    pausedChannels={pausedChannels}
                  />
                )}
              </PageNotices>
              <PageHeader>
                <PageHeader.Row>
                  <PublishPageTitle />

                  <PageHeader.Actions>
                    {isViewOnly && <ViewOnlyHeaderBadge />}
                    <FeedbackWidget
                      id="all-channels-1"
                      className={pageHeaderStyles.hideOnNarrow}
                    />

                    <div className={pageHeaderStyles.hideOnNarrow}>
                      <PostListOrCalendarViewToggle
                        className={styles.toggleViewAction}
                      />
                    </div>
                    {showComposerTrigger && (
                      <NewPostHeaderActions
                        wrapTrigger={(trigger): JSX.Element => (
                          <NewPostComposerTrigger
                            cta="publish-allChannels-header-newPost-1"
                            channels={
                              effectiveChannelIds.length
                                ? effectiveChannelIds
                                : undefined
                            }
                            prefillPostData={{
                              tags: filterOutUntaggedId(tagsQueryParam),
                            }}
                          >
                            {trigger}
                          </NewPostComposerTrigger>
                        )}
                      />
                    )}
                  </PageHeader.Actions>
                </PageHeader.Row>
                <PageHeader.Row>
                  <PostTabsResponsive
                    tabs={tabs}
                    value={tab}
                    onChange={(value): void => void setTab(value)}
                    countLimit={tabCountLimit}
                    countLoading={
                      countsLoading || channelLessDraftsCountLoading
                    }
                  />

                  <PageHeader.Actions>
                    <div className={pageHeaderStyles.hideOnNarrow}>
                      <div className={styles.hideForQueueActions}>
                        <FilterByChannel
                          channels={channels}
                          value={channelFilterSelection}
                          onSelect={setChannelFilter}
                          disabledChannelIds={disabledChannelIds}
                          getDisabledChannelReason={(id) =>
                            getDisabledPublishChannelReason(
                              channels.find((channel) => channel.id === id),
                            )
                          }
                          viewOnlyChannelIds={viewOnlyChannelIds}
                          viewOnlyChannelReason={
                            VIEW_ONLY_PUBLISH_CHANNEL_TOOLTIP
                          }
                          showNoChannelsOption={tab === 'drafts'}
                        />
                      </div>
                    </div>
                    <div className={pageHeaderStyles.hideOnNarrow}>
                      <div className={styles.hideForQueueActions}>
                        <FilterByTag
                          value={tagsQueryParam}
                          onSelect={setTagFilter}
                        />
                      </div>
                    </div>

                    <div className={pageHeaderStyles.hideOnNarrow}>
                      <div className={styles.hideForQueueActions}>
                        <AllChannelsTimeZoneSelect />
                      </div>
                    </div>

                    <div className={styles.showQueueActions}>
                      <QueueActions
                        channels={channels}
                        selectedChannels={channelFilterSelection}
                        onChannelSelect={setChannelFilter}
                        selectedTags={tagsQueryParam}
                        onTagSelect={setTagFilter}
                        triggerIcon={<ListFilterIcon />}
                        disabledChannelIds={disabledChannelIds}
                        showNoChannelsOption={tab === 'drafts'}
                      />
                    </div>
                    <div className={pageHeaderStyles.hideOnWide}>
                      <PostListOrCalendarViewToggle />
                    </div>
                  </PageHeader.Actions>
                </PageHeader.Row>
              </PageHeader>

              <PageLayout.Container size="narrow" className={styles.content}>
                {tab === 'queue' && (
                  <TrackRender
                    componentName="AllChannelsQueue"
                    organizationId={organizationId}
                    product="publish"
                    platform="new_publish"
                  />
                )}
                {selectionHasNoAccessibleChannels ? (
                  <AccessDenied description="You don't have access to the selected channels on Publish. Adjust the channel filter or reach out to your organization admin to request access." />
                ) : (
                  tabs.map((tabConfig) => {
                    // Every post belongs to a channel, so a pure "No
                    // channels" selection (no real channel alongside it)
                    // only has anything to show on Drafts.
                    if (
                      isPureNoChannelsSelection &&
                      tabConfig.id !== 'drafts'
                    ) {
                      return (
                        <PostTabs.Panel key={tabConfig.id} value={tabConfig.id}>
                          <NoChannelsFilterEmptyState
                            onViewDrafts={() => void setTab('drafts')}
                          />
                        </PostTabs.Panel>
                      )
                    }

                    const standardEmptyState = filterApplied ? (
                      <FilteredPostListEmptyState
                        onClearFilters={clearFilters}
                      />
                    ) : (
                      <PostEmptyStateByTab
                        tab={tabConfig.id}
                        hasChannels={channels.length > 0}
                        canManageTeamMembers={canManageTeamMembers}
                        isOwner={isOwner}
                      >
                        {showComposerTrigger && channels.length > 1 && (
                          <NewPostComposerTrigger
                            cta="publish-allChannels-emptyState-newPost-1"
                            channels={
                              effectiveChannelIds.length
                                ? effectiveChannelIds
                                : undefined
                            }
                          >
                            <Button size="large">
                              <PlusIcon /> New Post
                            </Button>
                          </NewPostComposerTrigger>
                        )}
                        {channels.length === 0 && isOwner && (
                          <>
                            <OpenChannelStoreFrontTrigger cta="publish-allChannels-emptyState-connectAChannel-1">
                              <Button variant="primary" size="medium">
                                <PlusIcon /> Connect a Channel
                              </Button>
                            </OpenChannelStoreFrontTrigger>
                            <TeamMemberInviteUpgradeCTA />
                          </>
                        )}
                        {channels.length === 0 && !isOwner && (
                          <>
                            {canManageChannels && (
                              <OpenChannelStoreFrontTrigger cta="publish-allChannels-emptyState-connectAChannel-1">
                                <Button variant="primary" size="medium">
                                  <PlusIcon /> Connect a Channel
                                </Button>
                              </OpenChannelStoreFrontTrigger>
                            )}
                            {canManageTeamMembers && <ManageChannelAccessCTA />}
                          </>
                        )}
                        {showComposerTrigger && channels.length === 1 && (
                          <NewPostComposerTrigger
                            cta="publish-allChannels-emptyState-createYourNextPost-1"
                            channels={
                              effectiveChannelIds.length
                                ? effectiveChannelIds
                                : 'all'
                            }
                            prefillPostData={{
                              tags: filterOutUntaggedId(tagsQueryParam),
                            }}
                          >
                            <Button size="large" variant="secondary">
                              <PlusIcon /> Create Your Next Post
                            </Button>
                          </NewPostComposerTrigger>
                        )}
                      </PostEmptyStateByTab>
                    )
                    const isValueLedQueueCandidate =
                      tabConfig.id === 'queue' &&
                      !filterApplied &&
                      canShowValueLedQueue
                    const emptyState =
                      isValueLedQueueCandidate && channelsLoading ? (
                        <PostListSkeleton />
                      ) : isValueLedQueueCandidate ? (
                        <ValueLedQueueEmptyState
                          channels={valueLedQueueChannels}
                          onboardingServices={onboardingChannelFocus}
                          fallback={standardEmptyState}
                        />
                      ) : (
                        standardEmptyState
                      )

                    // On every other tab, an empty selection means "no
                    // filter" and collapses to `undefined` (show every
                    // channel). On Drafts with "No channels" active, an empty
                    // selection means "zero real channels" and must stay `[]`
                    // — collapsing it would silently widen back to every
                    // channel's drafts instead of channel-less content items
                    // only. @see includeChannellessDrafts
                    const includeChannellessDraftsForTab =
                      tabConfig.id === 'drafts' && isNoChannelsSelected
                    const channelIdsForTab = includeChannellessDraftsForTab
                      ? effectiveChannelIds
                      : effectiveChannelIds.length
                        ? effectiveChannelIds
                        : undefined

                    return (
                      <PostTabs.Panel key={tabConfig.id} value={tabConfig.id}>
                        <VisuallyHidden as="h2">
                          {SECTION_HEADING_BY_POST_TAB[tabConfig.id]}
                        </VisuallyHidden>
                        {tabConfig.id === 'drafts' && (
                          <div className={styles.guestDraftImportNotice}>
                            <GuestDraftImportNotice />
                          </div>
                        )}
                        <PostList
                          // this is needed so that each tab loads independently and data is not cached between them
                          key={tabConfig.id}
                          status={tabConfig.id ?? 'queue'}
                          channelIds={channelIdsForTab}
                          tagIds={
                            tagsQueryParam.length ? tagsQueryParam : undefined
                          }
                          includeChannellessDrafts={
                            includeChannellessDraftsForTab
                          }
                          showChannelNotices={true}
                          emptyState={emptyState}
                        />
                      </PostTabs.Panel>
                    )
                  })
                )}
              </PageLayout.Container>
            </PageLayout>
          </PostTabs>
        </RegisteredBannersProvider>
        {/* Outlet for modals and dialogs */}
        <Outlet />
      </TimezoneProvider>
    </>
  )
}

export const PublishPageTitle = ({
  showSaveView = true,
}: {
  showSaveView?: boolean
}): JSX.Element => (
  <ProductPageTitle
    icon={<CalendarIcon size={24} />}
    title="Publish"
    showSaveView={showSaveView}
  />
)
